import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Signs the user into an embedded app (the beta app behind Smart Insights V3) through its own Microsoft SSO
 * without showing its login screen, then opens `targetUrl` in the same iframe.
 *
 * 1. Fetch the app's authorize URL and load it with `prompt=none` + `login_hint` (the Microsoft account
 *    the user signed into SmartCHG with), so Microsoft redirects straight back to the app as that same
 *    account, which stores its token. Without a hint, Microsoft uses the browser's current session.
 * 2. When that load finishes, give the app a moment to persist the token, then switch to `targetUrl`.
 * 3. `ready` turns true once `targetUrl` has loaded; keep the iframe hidden until then.
 *
 * Plug-out: stop calling this hook and load `targetUrl` directly.
 */
const AUTHORIZE_URL_PATH = '/auth/sso/authorize-url?localCallback=false';
const TOKEN_SETTLE_MS = 1_500;

type Phase = 'signing-in' | 'opening' | 'ready';

const noop = () => {};

export const useSilentSsoIframe = (
  targetUrl: string | null | undefined,
  enabled: boolean,
  loginHint?: string | null
) => {
  const [src, setSrc] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('signing-in');
  const settleTimerRef = useRef<number | null>(null);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current === null) return;
    window.clearTimeout(settleTimerRef.current);
    settleTimerRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled || !targetUrl) return undefined;

    let cancelled = false;
    setSrc(null);
    setPhase('signing-in');

    // Plain fetch, not the app `api` client: SmartCHG's token must never be sent to the embedded app.
    fetch(`${new URL(targetUrl).origin}${AUTHORIZE_URL_PATH}`, { credentials: 'omit' })
      .then((response) => {
        if (!response.ok) throw new Error(`authorize-url returned ${response.status}`);
        return response.json() as Promise<{ authorizationUrl?: string }>;
      })
      .then(({ authorizationUrl }) => {
        if (cancelled) return;
        if (!authorizationUrl) throw new Error('authorize-url returned no authorizationUrl');
        const hint = loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : '';
        setSrc(`${authorizationUrl}&prompt=none${hint}`);
      })
      .catch(() => {
        if (cancelled) return;
        // SSO could not start — open the target directly (the app's own login if signed out).
        setSrc(targetUrl);
        setPhase('opening');
      });

    return () => {
      cancelled = true;
      clearSettleTimer();
    };
  }, [clearSettleTimer, enabled, loginHint, targetUrl]);

  const onLoad = useCallback(() => {
    if (phase === 'signing-in') {
      if (settleTimerRef.current !== null || !targetUrl) return;
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = null;
        setSrc(targetUrl);
        setPhase('opening');
      }, TOKEN_SETTLE_MS);
      return;
    }
    if (phase === 'opening') setPhase('ready');
  }, [phase, targetUrl]);

  if (!enabled) return { src: targetUrl ?? null, ready: true, onLoad: noop };
  return { src, ready: phase === 'ready', onLoad };
};
